import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { ReturnReasons, Footer, StickyFooter } from '@narvar/nth-kit-returns-headless';
import { NthContextProvider } from '../../NthContextProvider';
import s from '../../exchangeSidebar.module.css';
import ButtonMenu from '../../../../react-components/ButtonMenu';
import ReturnsSelect from '../../../../react-components/ReturnsSelect';
import SidebarSectionTitle from '../../../../react-components/SidebarSectionTitle/SidebarSectionTitle';
import ExchangeComment from '../../components/ExchangeComment';
import { handlePhotoUpload } from '../../../../lib/reasons';
import PhotoUploader from '../../../../react-components/PhotoUploader';
import { setExchangeTo, setUnevenExchangeStep, STEPS } from '../../../../actions/unevenExchanges';
import ItemInfo from '../../../../react-components/SidebarItemDetails/SidebarItemDetails';
import { formatCurrencyValue, getCurrencyExponent } from '../../../../lib/currencyFormatters';
import ChangeIndicator from '../../../../react-components/ItemCard/ItemProperties/ChangeIndicator';
import { mapToHeadlessItem } from './helpers';

export const mapStateToProps = ({
  claims: { enabled: claimsEnabled } = {},
  page: { productType, languageCode, locale } = {},
  returnsSettings: {
    [productType]: {
      disableSidebarComments,
      sidebar_item_attrs: sidebarItemAttributes,
      exchange: { hide_sold_out_inventory: hideSoldOutInventory = false } = {},
    } = {},
  } = {},
  photoUpload: { isUploading, images } = {},
  language,
  language: {
    'returns.step_1.exchange_eligibility_only_header': exchangeOnlyMessage,
    'returns.step_1.exchange_item_out_of_stock': outOfStockErrorMessage,
    'returns.step_1.sidepanel_exchange_additional_comments': exchangeCommentTitle,
    'returns.step_1.sidepanel_exchange_comments_description': exchangeCommentDescription,
    'returns.step_1.sidepanel_next': submitBtnText,
    'returns.step_1.sidepanel_dont_exchange': cancelExchangeBtnText,
    'returns.step_1.sidepanel_exchange_reason_title': exchangeReasonTitle,
  } = {},
  itemsPage: { isFetchingAddCartItem, isFetchingRemoveCartItem } = {},
  unevenExchanges: { exchangeOptions, exchangeVariants, quantityOptions, step, exchangeTo },
}) => ({
  hideSoldOutInventory,
  productChoiceTitle: claimsEnabled
    ? language['returns.step_1.item_sidepanel_product_choice']
    : language['returns.step_1.sidepanel_product_choice'],
  exchangeOnlyMessage,
  outOfStockErrorMessage,
  exchangeCommentTitle,
  exchangeCommentDescription,
  submitBtnText,
  cancelExchangeBtnText,
  exchangeReasonTitle,
  disableSidebarComments,
  isUploading,
  images,
  languageCode,
  locale,
  isFetchingAddCartItem,
  isFetchingRemoveCartItem,
  exchangeOptions,
  exchangeVariants,
  quantityOptions,
  step,
  sidebarItemAttributes,
  exchangeTo,
});

const UnevenExchanges = ({
  dispatch,
  toggleSidebar,
  hideSoldOutInventory,
  returnEligibility,
  productChoiceTitle,
  exchangeOnlyMessage,
  productSelectMenu,
  productSelectOnClick,
  checkSelected,
  itemSaved,
  outOfStockError,
  outOfStockErrorMessage,
  language,
  itemQuantity,
  exchangeOptions,
  exchangeVariants,
  quantityOptions,
  quantitySelectOnChange,
  returnQuantity,
  isCommentBoxCountDisabled,
  exchangeComment,
  maxCommentLength,
  commentPlaceholder,
  commentHidden,
  commentRequired,
  exchangeCommentDescription,
  exchangeCommentOnChange,
  exchangeReasons,
  nestedReasons,
  checkReasonIsSelected,
  selectedReason,
  selectedNestedReason,
  exchangeReasonTitle,
  nestedReasonsTitle,
  handleSelectReason,
  resetReasons,
  disableSidebarComments,
  isUploading,
  images,
  photoUploadRequired,
  photoUploadEnabled,
  photoUploadTitle,
  photoUploadPlaceholder,
  photoUploadMaxFiles,
  languageCode,
  locale,
  currentItem,
  onSubmit,
  onCancel,
  step,
  sidebarItemAttributes,
  exchangeTo,
}) => {
  useEffect(() => {
    if (itemQuantity === 1) {
      quantitySelectOnChange({
        value: 1,
      });
    }
  }, [itemQuantity]);

  const footerRef = useRef(null);
  const isFetching = isUploading;
  const quantitySortOrder = Array(itemQuantity)
    .fill(0)
    .map((qty, index) => index + 1);

  const hasExchangeReasons = exchangeReasons?.length > 0;
  const parentReasonSelectionRequired = hasExchangeReasons && !selectedReason;
  const nestedReasonSelectionRequired = nestedReasons && !selectedNestedReason;
  const showCommentBox = (!commentHidden || commentRequired) && !disableSidebarComments;
  const commentIsRequired = commentRequired && !exchangeComment;
  const disableSubmit =
    !returnQuantity ||
    parentReasonSelectionRequired ||
    nestedReasonSelectionRequired ||
    commentIsRequired ||
    isUploading ||
    (photoUploadRequired && images.length < 1);

  const itemPriceDisplay = formatCurrencyValue({ value: currentItem.item_price, locale });
  const exchangeToPrice = exchangeTo?.price?.value;

  if (!currentItem || !exchangeOptions || !exchangeVariants) return null;
  return (
    <NthContextProvider>
      <div className={s.panel_content} aria-label="Side Panel Content">
        {step === STEPS.RETURN_ITEM_DETAILS ? (
          <ItemInfo
            item={currentItem}
            displayImg
            itemProperties={
              step === STEPS.RETURN_ITEM_DETAILS ? sidebarItemAttributes || ['Color', 'Size'] : []
            }
            language={language}
          />
        ) : (
          <div className={s.uneven_exchange_header_section}>
            <SidebarSectionTitle
              index={0}
              title={language['returns.step_1.sidepanel_uneven_exchange_title']}
            />
            <img
              style={{
                width: '100px',
                height: 'auto',
                padding: '10px 0',
              }}
              src={currentItem.product_image_url}
              alt=""
              onError={(event) => {
                event.target.src = '/r2/img/blindItemImage.svg';
              }}
            />
            <div>
              {language['returns.item.price_label']}
              {exchangeToPrice ? (
                <ChangeIndicator
                  before={itemPriceDisplay}
                  after={formatCurrencyValue({
                    value: exchangeToPrice / getCurrencyExponent('USD'),
                    locale,
                  })}
                />
              ) : (
                itemPriceDisplay
              )}
            </div>
          </div>
        )}
      </div>

      {step === STEPS.RETURN_ITEM_DETAILS && (
        <>
          {returnEligibility && <SidebarSectionTitle index={0} title={productChoiceTitle} />}
          {returnEligibility ? (
            <ButtonMenu
              menuItems={[productSelectMenu.find(({ type }) => type === 'exchange')]}
              itemKey="type"
              propName="displayName"
              itemClick={productSelectOnClick}
              checkSelected={checkSelected}
              disabled={Boolean(itemSaved)}
            />
          ) : (
            <div> {exchangeOnlyMessage} </div>
          )}

          {outOfStockError && <div className={s.error_message}> {outOfStockErrorMessage} </div>}

          {!outOfStockError && hasExchangeReasons && (
            <React.Fragment>
              <SidebarSectionTitle index={0} title={exchangeReasonTitle} />
              <ButtonMenu
                menuItems={selectedReason ? [selectedReason] : exchangeReasons}
                itemKey="reason_category"
                propName="reason_category"
                itemClick={(index) => {
                  if (isFetching) return;
                  if (selectedReason) {
                    resetReasons();
                  } else {
                    handleSelectReason(index);
                  }
                }}
                checkSelected={checkReasonIsSelected}
              />
            </React.Fragment>
          )}

          {nestedReasons && (
            <div className="nested-reasons-container">
              <SidebarSectionTitle
                title={nestedReasonsTitle || language['returns.step_1.sidepanel_tell_us_more']}
              />
              <ButtonMenu
                menuItems={nestedReasons}
                itemKey="return_reason_code_id"
                propName="display_name"
                itemClick={(index) => {
                  if (isFetching) return;
                  handleSelectReason(index);
                }}
                checkSelected={checkReasonIsSelected}
              />
            </div>
          )}

          {itemQuantity > 1 && (
            <ReturnsSelect
              exchangeCriteria="quantity"
              optionDisabledLabel="qty_unavailable_label"
              exchangeOptions={{ quantity: quantityOptions, quantitySortOrder }}
              language={language}
              onChange={quantitySelectOnChange}
              selectedOptions={{
                quantity: returnQuantity,
              }}
              hideUnavailable={hideSoldOutInventory}
            />
          )}
          {photoUploadEnabled && (
            <PhotoUploader
              title={photoUploadTitle}
              placeholder={photoUploadPlaceholder}
              onDrop={(acceptedFiles, rejectedFiles) =>
                handlePhotoUpload({
                  acceptedFiles,
                  rejectedFiles,
                  images,
                  photoUploadMaxFiles,
                  dispatch,
                  language,
                  languageCode,
                })
              }
            />
          )}
          {hasExchangeReasons && showCommentBox && (
            <ExchangeComment
              title={
                commentRequired
                  ? language['returns.step_1.sidepanel_exchange_additional_comments_mandatory']
                  : language['returns.step_1.sidepanel_exchange_additional_comments']
              }
              isCommentBoxCountDisabled={isCommentBoxCountDisabled}
              exchangeComment={exchangeComment}
              maxCommentLength={maxCommentLength}
              commentPlaceholder={commentPlaceholder || exchangeCommentDescription}
              onChange={exchangeCommentOnChange}
            />
          )}

          <StickyFooter absolute removePadding="inline">
            <Footer
              ref={footerRef}
              above
              buttons={{
                leftButton: {
                  type: 'back',
                  label: language['returns.step_1.sidepanel_uneven_exchange_back'],
                  disabled: false,
                  outline: true,
                  big: true,
                },
                rightButton: {
                  type: 'next',
                  label: language['returns.step_1.sidepanel_uneven_exchange_next'],
                  disabled: disableSubmit,
                  outline: false,
                  big: true,
                },
              }}
              onEvent={(e) => {
                if (e.value === 'back') {
                  if (itemSaved) {
                    onCancel();
                  } else {
                    toggleSidebar();
                  }
                }
                if (e.value === 'next') {
                  dispatch(setUnevenExchangeStep(STEPS.EXCHANGE_ITEM_PICKER));
                }
              }}
            />
          </StickyFooter>
        </>
      )}

      <div
        className={s.headless_container}
        style={{
          // Pre-rendering into the DOM is required so that SwatchesList can calculate the correct image heights
          display: step === STEPS.EXCHANGE_ITEM_PICKER ? 'block' : 'none',
        }}
      >
        <ReturnReasons
          activeStep
          defaultType="exchange"
          orderItem={mapToHeadlessItem({
            item: currentItem,
            quantity: returnQuantity,
            exchangeOptions,
          })}
          exchangeOptions={exchangeOptions}
          exchangeVariants={exchangeVariants.map((variant) => {
            const available = variant.available && variant.quantity >= returnQuantity;
            return {
              ...variant,
              sku: undefined,
              available,
              availabilityStatus: available
                ? language['returns.step_1.sidepanel_uneven_exchange_in_stock']
                : language['returns.step_1.sidepanel_uneven_exchange_out_of_stock'],
            };
          })}
          stepsProps={{
            exchanges: { enabled: true, simpleFlow: true, shopNow: false },
            pictures: { enabled: false },
            comments: { required: false },
          }}
          layout="footerAbsolute"
          loading={false}
          onChange={(data) => {
            dispatch(setExchangeTo(data.value?.exchangeTo));
          }}
          onClose={() => dispatch(setUnevenExchangeStep(STEPS.RETURN_ITEM_DETAILS))}
          onSubmit={onSubmit}
        />
      </div>
    </NthContextProvider>
  );
};

UnevenExchanges.defaultProps = {
  hideSoldOutInventory: false,
  returnEligibility: false,
  productChoiceTitle: '',
  exchangeOnlyMessage: '',
  productSelectMenu: [],
  itemSaved: null,
  outOfStockError: false,
  options: [],
  itemQuantity: null,
  returnQuantity: 0,
  isCommentBoxCountDisabled: false,
  exchangeCommentTitle: '',
  exchangeComment: '',
  maxCommentLength: '',
  commentPlaceholder: '',
  commentHidden: undefined,
  commentRequired: undefined,
  exchangeCommentDescription: '',
  outOfStockErrorMessage: '',
  submitBtnText: '',
  cancelExchangeBtnText: '',
  exchangeReasons: undefined,
  nestedReasons: undefined,
  selectedReason: undefined,
  selectedNestedReason: undefined,
  exchangeReasonTitle: '',
  nestedReasonsTitle: '',
  disableSidebarComments: undefined,
  photoUploadRequired: undefined,
  photoUploadEnabled: false,
  photoUploadPlaceholder: '',
  photoUploadTitle: '',
  photoUploadMaxFiles: undefined,
  isFetchingAddCartItem: false,
  isFetchingRemoveCartItem: false,
  exchangeOptions: undefined,
  exchangeVariants: undefined,
  quantityOptions: undefined,
  sidebarItemAttributes: undefined,
  exchangeTo: undefined,
};

UnevenExchanges.propTypes = {
  dispatch: PropTypes.func.isRequired,
  hideSoldOutInventory: PropTypes.bool,
  returnEligibility: PropTypes.bool,
  productChoiceTitle: PropTypes.string,
  productSelectMenu: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      displayName: PropTypes.string,
    }),
  ),
  productSelectOnClick: PropTypes.func.isRequired,
  checkSelected: PropTypes.func.isRequired,
  itemSaved: PropTypes.object,
  outOfStockError: PropTypes.bool,
  options: PropTypes.array,
  language: PropTypes.object.isRequired,
  itemQuantity: PropTypes.number,
  quantitySelectOnChange: PropTypes.func.isRequired,
  returnQuantity: PropTypes.number,
  isCommentBoxCountDisabled: PropTypes.bool,
  exchangeCommentTitle: PropTypes.string,
  exchangeComment: PropTypes.string,
  maxCommentLength: PropTypes.number,
  commentPlaceholder: PropTypes.string,
  commentHidden: PropTypes.bool,
  commentRequired: PropTypes.bool,
  exchangeCommentDescription: PropTypes.string,
  exchangeCommentOnChange: PropTypes.func.isRequired,
  exchangeOnlyMessage: PropTypes.string,
  outOfStockErrorMessage: PropTypes.string,
  submitBtnText: PropTypes.string,
  cancelExchangeBtnText: PropTypes.string,
  exchangeReasons: PropTypes.arrayOf(PropTypes.object),
  nestedReasons: PropTypes.arrayOf(PropTypes.object),
  checkReasonIsSelected: PropTypes.func.isRequired,
  selectedReason: PropTypes.object,
  selectedNestedReason: PropTypes.object,
  exchangeReasonTitle: PropTypes.string,
  nestedReasonsTitle: PropTypes.string,
  handleSelectReason: PropTypes.func.isRequired,
  resetReasons: PropTypes.func.isRequired,
  disableSidebarComments: PropTypes.bool,
  isUploading: PropTypes.bool.isRequired,
  images: PropTypes.array.isRequired,
  photoUploadRequired: PropTypes.bool,
  photoUploadEnabled: PropTypes.bool,
  photoUploadPlaceholder: PropTypes.string,
  photoUploadTitle: PropTypes.string,
  photoUploadMaxFiles: PropTypes.number,
  languageCode: PropTypes.string.isRequired,
  isFetchingAddCartItem: PropTypes.bool,
  isFetchingRemoveCartItem: PropTypes.bool,
  toggleSidebar: PropTypes.func.isRequired,
  exchangeOptions: PropTypes.arrayOf(PropTypes.object),
  exchangeVariants: PropTypes.arrayOf(PropTypes.object),
  quantityOptions: PropTypes.object,
  locale: PropTypes.string.isRequired,
  currentItem: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  step: PropTypes.string.isRequired,
  sidebarItemAttributes: PropTypes.arrayOf(PropTypes.string),
  exchangeTo: PropTypes.object,
};

export default connect(mapStateToProps)(UnevenExchanges);
